import React from "react";

// Utilities
export function ymd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function parseYmd(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

export default function CalendarGrid({
  monthDate,
  onPrev,
  onNext,
  onSelectDay,
  selectedYmd,
  tasksByDay = {},       // { "YYYY-MM-DD": [tasks] }
  campaignsSpanning = [], // list of campaigns with {id, name, start_date, end_date, color}
  legend,
}) {
  const som = startOfMonth(monthDate);
  const eom = endOfMonth(monthDate);
  const startWeekday = som.getDay(); // 0=Sun
  const daysInMonth = eom.getDate();
  const todayY = ymd(new Date());
  const monthLabel = monthDate.toLocaleString("en-US", { month: "long", year: "numeric" });

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="iz-card overflow-hidden">
      <div className="flex items-center justify-between p-5 border-b border-[#262626]">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">{monthLabel.split(" ")[0]}</p>
          <p className="font-display text-2xl text-white">{monthLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          {legend}
          <button onClick={onPrev} className="iz-btn-secondary px-3 py-1.5" data-testid="cal-prev">←</button>
          <button onClick={onNext} className="iz-btn-secondary px-3 py-1.5" data-testid="cal-next">→</button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-[#262626]">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
          <div key={d} className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 p-3 text-center border-r border-[#1a1a1a] last:border-r-0">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 auto-rows-[minmax(120px,1fr)]">
        {cells.map((c, idx) => {
          if (!c) return <div key={idx} className="border-r border-b border-[#1a1a1a] last:border-r-0 bg-[#080808]/50"/>;
          const y = ymd(c);
          const isToday = y === todayY;
          const isSelected = y === selectedYmd;
          const tasks = tasksByDay[y] || [];
          const campaigns = campaignsSpanning.filter(cam => y >= cam.start_date && y <= cam.end_date);
          return (
            <button
              type="button"
              key={idx}
              onClick={() => onSelectDay && onSelectDay(y)}
              className={`text-left border-r border-b border-[#1a1a1a] last:border-r-0 p-2 relative transition-colors ${
                isSelected ? "bg-[#d4af37]/10" : "hover:bg-[#0e0e0e]"
              }`}
              data-testid={`cal-day-${y}`}
            >
              <div className="flex items-start justify-between">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium ${
                  isToday ? "bg-[#d4af37] text-black" : (isSelected ? "text-[#d4af37]" : "text-gray-300")
                }`}>
                  {c.getDate()}
                </span>
                {tasks.length > 0 && (
                  <span className="text-[10px] font-mono text-[#d4af37]">{tasks.length}</span>
                )}
              </div>
              <div className="mt-1 space-y-1">
                {campaigns.slice(0,2).map(cam => (
                  <div key={cam.id} className="h-1.5 rounded-sm" style={{ background: cam.color || "#d4af37", opacity: 0.7 }} title={cam.name}/>
                ))}
                {tasks.slice(0,2).map(t => (
                  <div key={t.id} className="text-[10px] text-gray-300 truncate border-l-2 border-[#d4af37] pl-1.5" title={t.title}>{t.title}</div>
                ))}
                {tasks.length > 2 && <p className="text-[9px] text-gray-500">+{tasks.length - 2} more</p>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
